// Lion Auto's manual overrides: listings of ours a pricer puts in a group by
// hand (criteria.includeIds: past its sections, Qty, rows and public-notes
// filters, and taken from another group holding it) or takes out of it
// (criteria.excludeIds). Saved with the group's criteria like any filter, so
// a change re-matches. The two lists never share an id; excluding a ranked
// listing also takes it out of the ladder. An included listing still waits
// for approval before Auto prices it (the server's needs-approval queue).
// A market listing a pricer takes out of the comps by hand goes in
// criteria.excludeCompIds, by its Vivid listing id: comps are shared, so it
// never touches our listings' lists, and our listings sold (liveIds) never
// drop one.
// Here: the lists as the server takes them, the table's Include / Exclude
// and Not a comp toggles, which button a row gets and which rows of ours the
// table shows. Pure; tested in lionAutoOverrides.test.js.
import { matchesSelectedSection } from './lionAutoListings';

// The server's cap per list
export const MAX_OVERRIDE_IDS = 500;
// A button or rank cell waiting for the live preview to catch up with the
// criteria
export const UPDATING_TITLE = 'Updating the group…';

const idOf = (v) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};
const byNumber = (a, b) => a - b;

/**
 * Positive integer ids, deduped, at most MAX_OVERRIDE_IDS (the first ones
 * given), in number order: the lists are sets, so a toggle and its undo
 * give the same criteria back. With liveIds (a Set of our listings' ids as
 * strings), ids no longer among them (sold) drop out; null = not known,
 * keep all.
 */
export function cleanOverrideIds(ids, liveIds = null) {
  const out = [];
  const seen = new Set();
  for (const v of Array.isArray(ids) ? ids : []) {
    const id = idOf(v);
    if (id === null || seen.has(id) || (liveIds && !liveIds.has(String(id)))) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_OVERRIDE_IDS) break;
  }
  return out.sort(byNumber);
}

/**
 * The criteria as a save sends them: ids no longer among our listings
 * (liveIds, a Set of string ids; null = not known, nothing drops) out of
 * both hand lists, as a toggle already does. excludeCompIds (Vivid ids, not
 * ours) passes as it is.
 */
export function withLiveOverrides(criteria, liveIds) {
  const c = criteria || {};
  return {
    ...c,
    includeIds: cleanOverrideIds(c.includeIds, liveIds),
    excludeIds: cleanOverrideIds(c.excludeIds, liveIds),
  };
}

// One click on list `key`: a listing in it leaves, one not in it joins (and
// leaves the other list). The same criteria come back when nothing changes
// (a junk id, or the list is full).
function toggleIn(criteria, inventoryId, key, otherKey, liveIds) {
  const id = idOf(inventoryId);
  if (id === null) return criteria;
  const c = criteria || {};
  const had = cleanOverrideIds(c[key]).includes(id);
  const list = cleanOverrideIds(c[key], liveIds).filter(x => x !== id);
  if (!had && list.length >= MAX_OVERRIDE_IDS) return criteria;
  return {
    ...c,
    [key]: had ? list : [...list, id].sort(byNumber),
    [otherKey]: cleanOverrideIds(c[otherKey], liveIds).filter(x => x !== id),
  };
}

/**
 * Include / Remove include: the listing joins includeIds (and leaves
 * excludeIds), or leaves includeIds. Ids not in liveIds drop out of both
 * lists; the clicked one counts either way.
 */
export function toggleInclude(criteria, inventoryId, liveIds = null) {
  return toggleIn(criteria, inventoryId, 'includeIds', 'excludeIds', liveIds);
}

/**
 * Exclude / Undo exclude: the listing joins excludeIds (and leaves
 * includeIds and the ladder, the ranks after it closing up), or leaves
 * excludeIds (it is not ranked again). Ids not in liveIds drop out of both
 * lists.
 */
export function toggleExclude(criteria, inventoryId, liveIds = null) {
  const next = toggleIn(criteria, inventoryId, 'excludeIds', 'includeIds', liveIds);
  if (next === criteria) return criteria;
  const id = idOf(inventoryId);
  if (!next.excludeIds.includes(id) || !Array.isArray(next.ladderIds)) return next;
  return { ...next, ladderIds: next.ladderIds.filter(x => Number(x) !== id) };
}

const listHas = (list, id) => Array.isArray(list) && list.some(x => Number(x) === id);

/**
 * Where one listing of ours stands: 'included' / 'excluded' by hand (from
 * the criteria, so a click shows at once), else 'member' / 'nonmember' as
 * the match result says (out of section, or kept out by Qty, rows, notes or
 * another group). row: { id, isMember } — isMember null while the result is
 * not in, then a listing in neither list has no state (null).
 */
export function overrideStateFor(criteria, row) {
  const id = idOf(row?.id);
  if (id === null) return null;
  if (listHas(criteria?.includeIds, id)) return 'included';
  if (listHas(criteria?.excludeIds, id)) return 'excluded';
  if (row.isMember === true) return 'member';
  if (row.isMember === false) return 'nonmember';
  return null;
}

// The button per state; mode = the toggle it clicks
const ACTIONS = {
  member: {
    label: 'Exclude', mode: 'exclude', listKey: 'excludeIds',
    title: 'Take this listing out of the group by hand, whatever its filters say; it also leaves the price ladder',
  },
  included: {
    label: 'Remove include', mode: 'include', listKey: null,
    title: "Stop including this listing by hand: the group's sections and filters decide again",
  },
  excluded: {
    label: 'Undo exclude', mode: 'exclude', listKey: null,
    title: "Stop excluding this listing: the group's sections and filters decide again",
  },
  nonmember: {
    label: 'Include', mode: 'include', listKey: 'includeIds',
    title: 'Put this listing in the group by hand, past its sections and filters (and from another group holding it). '
      + 'Auto leaves it alone until it is approved.',
  },
};

/**
 * The table's override button for one listing of ours, or null for none:
 * { state, label, mode: 'include'|'exclude'|null, title, disabled } —
 * disabled when it would add to a list already at MAX_OVERRIDE_IDS, or
 * while the result on screen is not current (row.resultStale) for a state
 * that result decides (member / nonmember): right after Undo exclude the
 * old result still has the listing out, and an Include then would pin a
 * plain member. Included / excluded come from the criteria, so they stay.
 */
export function overrideActionFor(criteria, row) {
  const state = overrideStateFor(criteria, row);
  if (!state) return null;
  if (row.resultStale && (state === 'member' || state === 'nonmember')) {
    return { state, label: 'Updating…', mode: null, title: UPDATING_TITLE, disabled: true };
  }
  const { label, mode, title, listKey } = ACTIONS[state];
  const disabled = !!listKey && cleanOverrideIds(criteria?.[listKey]).length >= MAX_OVERRIDE_IDS;
  return { state, label, mode, title: disabled ? `${MAX_OVERRIDE_IDS} listings at most` : title, disabled };
}

/**
 * A listing included by hand that the result on screen does not have as a
 * member yet, while that result is not current (row: { id, isMember,
 * resultStale }): it can't be ranked until the group updates, and its Rank
 * cell says so rather than ignoring the click.
 */
export function awaitingInclude(criteria, row) {
  return overrideStateFor(criteria, row) === 'included' && row.isMember !== true && !!row.resultStale;
}

// A Vivid listing id as the server takes it (a positive safe integer), or null
const vidOf = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
};

/** The market listing (by its Vivid listing id) is taken out of the comps by hand */
export function isCompExcluded(criteria, vividListingId) {
  const vid = vidOf(vividListingId);
  return vid !== null && listHas(criteria?.excludeCompIds, vid);
}

/**
 * Not a comp / Use as comp: the Vivid listing id joins excludeCompIds, or
 * leaves it (MAX_OVERRIDE_IDS at most, in number order, as the other hand
 * lists). Never filtered by our live listings: these are not ours. The same
 * criteria come back when nothing changes (a junk id, or the list is full).
 */
export function toggleExcludedComp(criteria, vividListingId) {
  const vid = vidOf(vividListingId);
  if (vid === null) return criteria;
  const c = criteria || {};
  const list = cleanOverrideIds(c.excludeCompIds);
  const had = list.includes(vid);
  if (!had && list.length >= MAX_OVERRIDE_IDS) return criteria;
  return {
    ...c,
    excludeCompIds: had ? list.filter(x => x !== vid) : [...list, vid].sort(byNumber),
  };
}

const NO_VID_TITLE = 'Skybox sends no Vivid listing id for this listing, so it cannot be taken out by hand';
const COMP_ACTIONS = {
  comp: {
    label: 'Not a comp',
    title: "Take this listing out of the group's comps by hand, whatever its filters say; "
      + 'if it is the anchor comp, the next cheapest comp takes its place',
  },
  excluded: {
    label: 'Use as comp',
    title: "Stop excluding this listing: the group's sections and filters decide again",
  },
};

/**
 * The table's By hand button for one market listing while a draft is open:
 * { label, title, disabled } — Not a comp, or Use as comp when the criteria
 * take it out by hand. Disabled for a listing with no Vivid id (nothing to
 * save it by), while the result on screen is not current (row.resultStale:
 * the market rows are that result's) and when Not a comp would add to a list
 * already at MAX_OVERRIDE_IDS.
 */
export function compOverrideActionFor(criteria, row) {
  const vid = vidOf(row?.vividListingId);
  const excluded = vid !== null && isCompExcluded(criteria, vid);
  const { label, title } = COMP_ACTIONS[excluded ? 'excluded' : 'comp'];
  if (vid === null) return { label, title: NO_VID_TITLE, disabled: true };
  if (row.resultStale) return { label, title: UPDATING_TITLE, disabled: true };
  if (!excluded && cleanOverrideIds(criteria?.excludeCompIds).length >= MAX_OVERRIDE_IDS) {
    return { label, title: `${MAX_OVERRIDE_IDS} comps at most`, disabled: true };
  }
  return { label, title, disabled: false };
}

/** The included ids as strings (the table's Included chip and section filter) */
export function includedIdSet(criteria) {
  return new Set(cleanOverrideIds(criteria?.includeIds).map(String));
}

/**
 * The listings of ours the table shows wherever they sit, for the group on
 * screen (ids as strings): the ones it includes by hand (members wherever
 * they are) and, while it is edited, the ones it excludes by hand, so Undo
 * exclude stays in reach after their section leaves the draft.
 */
export function pinnedIdSet(criteria, editing = false) {
  const ids = includedIdSet(criteria);
  if (editing) for (const id of cleanOverrideIds(criteria?.excludeIds)) ids.add(String(id));
  return ids;
}

/**
 * Compare's section rule (matchesSelectedSection), except for listings of
 * ours: one in pinnedIds (pinnedIdSet(); null with no group on screen)
 * always shows, and with showAllOwn (the draft's "Show my other listings")
 * every one does. The market keeps the rule as is.
 */
export function matchesSelectionOrIncluded(listing, own, selectedSections, pinnedIds, showAllOwn = false) {
  if (own && (showAllOwn || (pinnedIds && pinnedIds.has(String(listing?.id))))) return true;
  return matchesSelectedSection(listing, selectedSections);
}

/**
 * A listing of ours outside the selected sections that the group does not
 * include by hand (includedIds: includedIdSet(), null = none): on screen
 * only as excluded by hand or by "Show my other listings", so the table
 * dims it. The market and the whole venue (no sections): never.
 */
export function isOutsideSelection(listing, own, selectedSections, includedIds) {
  if (!own || !selectedSections || selectedSections.length === 0) return false;
  if (includedIds && includedIds.has(String(listing?.id))) return false;
  return !matchesSelectedSection(listing, selectedSections);
}
